import {
  ShieldCheck,
  AlertCircle,
  FileCheck2,
  Users,
  History,
  ExternalLink,
  ArrowRight,
  CheckCircle2,
  Gauge,
} from "lucide-react";

const CATEGORIES = [
  { icon: AlertCircle, label: "Accident History", note: "Reported collision & damage events" },
  { icon: Gauge, label: "Odometer Check", note: "Rollback & mileage inconsistency screen" },
  { icon: FileCheck2, label: "Title Records", note: "Salvage, flood, lemon-law flags" },
  { icon: History, label: "Service History", note: "Recorded maintenance & inspections" },
  { icon: Users, label: "Ownership Timeline", note: "Number of previous owners & use type" },
];

export function WakzyCheckPanel({ vehicle }) {
  const hasReport = Boolean(vehicle.history_report_url);

  return (
    <div className="mb-10" data-testid="wakzycheck-panel">
      <div className="surface rounded-md p-6 relative overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-16 -right-16 w-64 h-64 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(212,175,55,0.10), transparent 70%)" }}
        />
        <div className="flex items-start justify-between gap-4 relative">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-5 h-5" style={{ color: "var(--wakzy-gold)" }} />
              <span className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>
                WakzyCheck<sup className="text-[9px] ml-0.5">™</sup> Vehicle History
              </span>
            </div>
            <h3 className="font-heading text-2xl text-white leading-tight">
              Every Wakzy vehicle is background-checked before it hits the lot.
            </h3>
            <p className="text-sm text-slate-400 mt-2 max-w-xl">
              We verify title status, mileage integrity, prior damage and ownership records for every unit.
              {hasReport
                ? " Tap the report link below for the full public history file on this VIN."
                : " Contact your Wakzy specialist for the full public history file on this VIN."}
            </p>
          </div>
          <span
            className="pill pill-available shrink-0"
            style={{
              background: "rgba(212,175,55,0.12)",
              color: "var(--wakzy-gold)",
              borderColor: "rgba(212,175,55,0.35)",
            }}
            data-testid="wakzycheck-verified-pill"
          >
            <ShieldCheck className="w-3 h-3 mr-1 inline" /> Verified
          </span>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6 relative">
          {CATEGORIES.map((c, i) => (
            <div
              key={i}
              className="border border-white/5 rounded-sm p-3 bg-black/20"
              data-testid={`wakzycheck-cat-${i}`}
            >
              <c.icon className="w-4 h-4 text-[#D4AF37] mb-2" />
              <div className="text-sm text-white leading-tight">{c.label}</div>
              <div className="text-[11px] text-slate-500 mt-1">{c.note}</div>
              <div className="flex items-center gap-1 text-[10px] mt-2" style={{ color: "var(--wakzy-gold)" }}>
                <CheckCircle2 className="w-3 h-3" /> Cleared by Wakzy
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3 relative">
          <div className="font-mono text-[10px] text-slate-500">VIN · {vehicle.vin}</div>
          <div className="flex-1" />
          {hasReport ? (
            <a
              href={vehicle.history_report_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold rounded-sm h-10 px-4 text-sm inline-flex items-center gap-2"
              data-testid="wakzycheck-view-report"
            >
              View Full Report <ExternalLink className="w-4 h-4" />
            </a>
          ) : (
            <a
              href="mailto:info@wakzyauto.com?subject=WakzyCheck%20report%20request"
              className="btn-gold rounded-sm h-10 px-4 text-sm inline-flex items-center gap-2"
              data-testid="wakzycheck-request-report"
            >
              Request Full Report <ArrowRight className="w-4 h-4" />
            </a>
          )}
        </div>
        <div className="text-[10px] text-slate-500 mt-3 relative">
          WakzyCheck™ summaries are provided for informational purposes only and are not a substitute for a physical inspection.
        </div>
      </div>
    </div>
  );
}
